import clsx from 'clsx';
import React, { useRef } from 'react';
import { useParams } from 'react-router';

import { Button } from '@/src/components/common/Elements/Button/Button';
import InlineImage from '@/src/components/common/Elements/Image/InlineImage';
import Link from '@/src/components/common/Elements/Link/Link';
import Popper from '@/src/components/common/Elements/Popper/Popper';
import { PROJECT_PAGE_ROUTES } from '@/src/components/project/Config/Config';
import { useToggle } from '@/src/hooks/useToggle';
import { generateRoutePath } from '@/src/routes';
import { cn } from '@/src/util/ui';

type ConnectedApp = {
  isAppEnabled: boolean;
  id?: string;
};

const ConnectedApp = ({ id = '', isAppEnabled }: ConnectedApp) => {
  const { projectId } = useParams<{ projectId: string }>();
  const connectedLabel = isAppEnabled ? 'Connected to: ' : 'Connect to: ';
  const buttonRef = useRef();
  const [showPopper, toggleShowPopper] = useToggle(false);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    toggleShowPopper(e);
  };

  const handleMouseLeave = () => {
    toggleShowPopper(null);
  };

  const href = generateRoutePath(PROJECT_PAGE_ROUTES.project_settings.path, {
    projectId: projectId,
    settingsRoute: 'apps',
  });

  return (
    <div className={clsx('tw-flex tw-items-center')}>
      <p className={clsx('tw-my-0 tw-mx-[10px]', 'tw-text-cfo-grey-600')}>{connectedLabel}</p>
      {isAppEnabled ? (
        <InlineImage
          id={`connected_app_tooltip`}
          src={`url(/images/quickbooks-icon1.png)`}
          tooltip={{ place: 'bottom-end', effect: 'solid', content: 'Quickbooks Online' }}
        />
      ) : (
        <Popper
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          handleShowPopper={() => toggleShowPopper(false)}
          mainClasses={clsx('tw-flex tw-items-center')}
          containerRef={buttonRef}
          popper={{
            type: 'tooltip',
            placement: 'bottom-end',
            showPopper: showPopper,
            styleClasses: cn(
              'tw-mt-[2px]',
              'tw-py-[5px] tw-px-[10px]',
              'tw-max-w-[189px] tw-w-full'
            ),
            content: (
              <span className={clsx('tw-text-white')}>
                No active integration setup. To update, go to{' '}
                <Link
                  href={href}
                  target={'_blank'}
                  className={clsx(
                    'tw-mt-[5px]',
                    'tw-text-cfo-blue-600 tw-underline',
                    'hover:!tw-text-cfo-blue-600 hover:!tw-underline' // (!) is not removed as it is to override the styles of anchor tag from vendor.css
                  )}
                >
                  Settings.
                </Link>
              </span>
            ),
          }}
        >
          <Button
            id={id}
            className={clsx('connectedAppButton--container', 'tw-mr-0', 'tw-cursor-default')}
            buttonRef={buttonRef}
            buttonClassSettings={{
              variant: 'inverse',
              size: 'sm',
              weight: 'bold',
              position: 'center',
            }}
            onClick={() => {}}
          >
            Apps
          </Button>
        </Popper>
      )}
    </div>
  );
};

export default ConnectedApp;
